import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isFciTopic } from "@/lib/parentAnalytics/fciFilter";
import { getPillarForCourse, getInstructorDisplayForCourse, PRACTICAL_SKILLS } from "@/lib/parentAnalytics/topics";
import {
  LAUNCH_MODULE_KEYS,
  LAUNCH_PATH_LESSON_IDS,
  TOTAL_LAUNCH_PATH_STEPS,
  moduleOfLaunchLesson,
  type LaunchModuleId,
} from "@/lib/parent/launchPathRoster";
import { deriveDataSufficiency, type DataSufficiency } from "@/lib/parent/dataSufficiency";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

export type TimeRange = "7d" | "30d" | "90d" | "all";

export interface UseParentAnalyticsParams {
  childId: string | null;
  startDate: Date;
  endDate: Date;
  range: TimeRange;
}

export interface TopicMasteryRow {
  topic: string;
  masteryScore: number;
  lessonsCompleted: number;
  lessonsTotal: number;
  averageQuizScore: number;
  quizCount: number;
  retakeCount: number;
  lastActivityAt: string | null;
  masteryStatus: "Not Started" | "Learning" | "Improving" | "Strong" | "Needs Review";
  instructor: string;
}

export interface SkillStatusRow {
  name: string;
  topic: string;
  status: "Not Introduced" | "Introduced" | "Practicing" | "Demonstrated" | "Needs Review";
  firstSeenAt: string | null;
  newThisMonth: boolean;
}

export interface CourseProgressRow {
  courseId: string;
  courseTitle: string;
  status: "Not Started" | "In Progress" | "Completed";
  completionPercentage: number;
  lessonsCompleted: number;
  lessonsTotal: number;
  pillar: "Finance" | "Economics" | "Entrepreneurship & Wealth";
  instructor: string;
  lastActivityAt: string | null;
  averageQuizScore: number | null;
  completedAt: string | null;
}

export interface QuizRow {
  id: string;
  quizTitle: string;
  topic: string;
  score: number;
  passed: boolean;
  attemptNumber: number;
  completedAt: string;
}

export interface ActivityEvent {
  id: string;
  type: "lesson" | "quiz" | "course" | "achievement";
  subtype: string; // LESSON_COMPLETED, QUIZ_PASSED, QUIZ_FAILED, QUIZ_RETAKEN, COURSE_STARTED, COURSE_COMPLETED, ACHIEVEMENT_EARNED
  title: string;
  detail: string;
  topic: string | null;
  score: number | null;
  attemptNumber: number | null;
  at: string;
}

export interface InsightItem {
  label: string;
  detail?: string;
}

export interface ActionRecommendation {
  priority: "Re-engage" | "Review Together" | "Celebrate" | "Explore Next";
  title: string;
  reason: string;
  prompt: string;
  topic?: string;
}

export interface PeriodDelta {
  lessonCount: number;
  averageQuizScore: number;
  activityDays: number;
  firstAttemptPassRate: number;
}

export interface ParentAnalyticsResult {
  lessonCount: number;
  cumulativeLessons: number;
  lessonsByDay: { date: string; count: number }[];
  lessonsByWeek: { week: string; count: number }[];
  priorPeriodLessonsByDay: { date: string; count: number }[];
  quizAttempts: QuizRow[];
  recentQuizzes: QuizRow[];
  quizScoreHistory: { date: string; score: number; quizTitle: string; topic: string }[];
  lessonActivity: { date: string; lessonName: string; topic: string | null }[];
  averageQuizScore: number;
  firstAttemptPassRate: number;
  retakeRate: number;
  topicMastery: TopicMasteryRow[];
  skillStatuses: SkillStatusRow[];
  courseProgress: CourseProgressRow[];
  coursesInProgress: number;
  coursesCompleted: number;
  pillarTotals: Record<"Finance" | "Economics" | "Entrepreneurship & Wealth", { inProgress: number; completed: number; total: number }>;
  activityDays: number;
  currentStreak: number;
  longestStreak: number;
  lastActiveDate: string | null;
  daysSinceLastActivity: number | null;
  activityCalendar: { date: string; count: number }[];
  mostActiveWeekday: string | null;
  avgLessonsPerWeek: number;
  weeklyActivity: { week: string; count: number }[];
  recentActivity: ActivityEvent[];
  strengths: InsightItem[];
  needsReview: InsightItem[];
  riskFlags: InsightItem[];
  actionRecommendations: ActionRecommendation[];
  periodDelta: PeriodDelta;
  strongestTopic: TopicMasteryRow | null;
  weakestTopic: TopicMasteryRow | null;
  totalLessonsAvailable: number;
  /** Phase 2 — beta-scoped counts (single source of truth for X/13 surfaces). */
  betaLessonsCompleted: number;          // 0-10, distinct beta lesson_ids touched
  betaModulesCompleted: number;          // 0-3, distinct beta module_keys passed
  betaPathCompleted: number;             // 0-13
  betaPathTotal: number;                 // always 13
  /** Per-beta-module activity rollup keyed by lesson_id assignment. */
  betaModuleStats: Record<LaunchModuleId, {
    lessonsCompleted: number;
    lessonsTotal: number;
    quizCount: number;
    averageQuizScore: number;
    moduleCompleted: boolean;
    lastActivityAt: string | null;
  }>;
  /** Centralized sufficiency for every card and the AI briefing gate. */
  dataSufficiency: DataSufficiency;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY_PILLAR_TOTALS = {
  Finance: { inProgress: 0, completed: 0, total: 0 },
  Economics: { inProgress: 0, completed: 0, total: 0 },
  "Entrepreneurship & Wealth": { inProgress: 0, completed: 0, total: 0 },
} as const;

function toIsoDate(d: Date | string): string {
  const dt = typeof d === "string" ? new Date(d) : d;
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoWeekLabel(d: Date): string {
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = target.getTime();
  target.setUTCMonth(0, 1);
  if (target.getUTCDay() !== 4) target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
  const weekNo = 1 + Math.ceil((firstThursday - target.getTime()) / 604800000);
  const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `W${weekNo} ${month}`;
}

function daysBetween(a: Date, b: Date): number {
  return Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000));
}

function buildDailyBuckets(start: Date, end: Date, dates: string[]): { date: string; count: number }[] {
  const map = new Map<string, number>();
  const cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const endUtc = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  while (cur <= endUtc) {
    map.set(toIsoDate(cur), 0);
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  for (const d of dates) {
    const key = toIsoDate(new Date(d));
    if (map.has(key)) map.set(key, (map.get(key) ?? 0) + 1);
  }
  return Array.from(map.entries()).map(([date, count]) => ({ date, count }));
}

function bucketByWeek(daily: { date: string; count: number }[]): { week: string; count: number }[] {
  const buckets = new Map<string, { count: number; first: Date }>();
  for (const row of daily) {
    const dt = new Date(row.date + "T00:00:00Z");
    const label = isoWeekLabel(dt);
    const existing = buckets.get(label);
    if (existing) existing.count += row.count;
    else buckets.set(label, { count: row.count, first: dt });
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[1].first.getTime() - b[1].first.getTime())
    .map(([week, v]) => ({ week, count: v.count }));
}

function computeStreaks(daily: { date: string; count: number }[], lastActive: string | null): { current: number; longest: number } {
  let longest = 0;
  let run = 0;
  for (const d of daily) {
    if (d.count > 0) {
      run += 1;
      longest = Math.max(longest, run);
    } else {
      run = 0;
    }
  }
  // current streak: count back from today
  let current = 0;
  if (lastActive) {
    const today = new Date();
    const lastDay = new Date(lastActive);
    const diff = Math.floor((today.getTime() - lastDay.getTime()) / 86400000);
    if (diff <= 1) {
      const set = new Set(daily.filter((d) => d.count > 0).map((d) => d.date));
      const cur = new Date();
      while (set.has(toIsoDate(cur))) {
        current += 1;
        cur.setUTCDate(cur.getUTCDate() - 1);
      }
    }
  }
  return { current, longest };
}

function mostActiveWeekdayName(daily: { date: string; count: number }[]): string | null {
  const counts = [0, 0, 0, 0, 0, 0, 0];
  let any = false;
  for (const d of daily) {
    if (d.count > 0) any = true;
    counts[new Date(d.date + "T00:00:00Z").getUTCDay()] += d.count;
  }
  if (!any) return null;
  const max = counts.indexOf(Math.max(...counts));
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][max];
}

function masteryStatusFor(row: { lessonsCompleted: number; lessonsTotal: number; averageQuizScore: number; retakeCount: number; quizCount: number }): TopicMasteryRow["masteryStatus"] {
  const { lessonsCompleted, lessonsTotal, averageQuizScore, retakeCount, quizCount } = row;
  if (lessonsCompleted === 0) return "Not Started";
  if (quizCount > 0 && averageQuizScore < 60) return "Needs Review";
  if (retakeCount >= 3) return "Needs Review";
  const ratio = lessonsTotal > 0 ? lessonsCompleted / lessonsTotal : 0;
  if (ratio >= 0.8 && averageQuizScore >= 75) return "Strong";
  if (ratio >= 0.5) return "Improving";
  return "Learning";
}

export function useParentAnalytics({ childId, startDate, endDate, range }: UseParentAnalyticsParams): ParentAnalyticsResult {
  const [data, setData] = useState<Omit<ParentAnalyticsResult, "refetch">>(() => emptyResult());
  const reqId = useRef(0);

  const fetchAll = useCallback(async () => {
    if (!childId) {
      setData((prev) => ({ ...emptyResult(), isLoading: false, error: prev.error }));
      return;
    }
    const myReq = ++reqId.current;
    setData((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const startIso = startDate.toISOString();
      const endIso = endDate.toISOString();
      const periodMs = endDate.getTime() - startDate.getTime();
      const priorStart = new Date(startDate.getTime() - periodMs);
      const priorEnd = startDate;

      // Pull all lessons for course_name + lesson_name resolution (small table).
      const [{ data: lessonRows, error: lessonsErr }, { data: coursesRows }] = await Promise.all([
        supabase.from("lessons").select("lesson_id, lesson_name, course_name"),
        supabase.from("courses").select("course_id, course_name"),
      ]);
      if (lessonsErr) throw lessonsErr;
      const lessonById = new Map<string, { lesson_name: string; course_name: string }>();
      for (const l of (lessonRows) || []) lessonById.set(l.lesson_id, { lesson_name: l.lesson_name, course_name: l.course_name });

      // Topic mastery — FCI filtered at the DB.
      const { data: topicMasteryRaw } = await supabase
        .from("topic_mastery")
        .select("topic, mastery_score, attempts_count, success_count, failure_count, last_seen_at")
        .eq("user_id", childId)
        .neq("topic", "Financial Confidence System")
        .not("topic", "ilike", "%FCI%")
        .not("topic", "ilike", "%Financial Confidence%")
        .not("topic", "ilike", "%Confidence Index%")
        .not("topic", "ilike", "%Confidence Score%")
        .not("topic", "ilike", "%Confidence Grade%");

      // User progress (no timestamp) — for cumulative lesson counts.
      const { data: userProgress } = await supabase
        .from("user_progress")
        .select("lesson_id, completed, score")
        .eq("user_id", childId)
        .eq("completed", true);

      // Quiz attempts for the selected window + all-time (for retakes / first attempt).
      const { data: quizAllRaw } = await supabase
        .from("quiz_attempts")
        .select("id, lesson_id, course_id, attempt_number, score_percent, passed, created_at")
        .eq("user_id", childId)
        .order("created_at", { ascending: true });

      // Course progress.
      const { data: courseRowsRaw } = await supabase
        .from("course_progress")
        .select("course_id, status, completion_percentage, started_at, completed_at, updated_at")
        .eq("user_id", childId);

      // Lesson-level activity (stage/question progress) — gives us real timestamps even before any quiz.
      const { data: lessonProgRaw } = await supabase
        .from("lesson_progress")
        .select("lesson_id, current_stage, correct_count, updated_at, created_at")
        .eq("user_id", childId)
        .order("updated_at", { ascending: true });

      // Achievements (joined for title).
      const { data: achRowsRaw } = await supabase
        .from("user_achievements")
        .select("id, unlocked_at, achievements(name, description)")
        .eq("user_id", childId)
        .order("unlocked_at", { ascending: false });

      // Beta module completions (3 keys only).
      const betaModuleKeys = Object.values(LAUNCH_MODULE_KEYS);
      const { data: moduleCompletionsRaw } = await supabase
        .from("module_completions")
        .select("module_key, passed, completed_at")
        .eq("user_id", childId)
        .in("module_key", betaModuleKeys);

      // Prior-period quiz attempts already in quizAllRaw — we filter in memory.

      if (myReq !== reqId.current) return;

      // ---------- Lessons by day (window) ----------
      // We use quiz_attempts.created_at as the proxy timestamp for "active learning" on a day,
      // since user_progress has no timestamp. Lesson activity counts unique lesson_id per day.
      const allAttempts = ((quizAllRaw) || []).filter((q) => {
        const meta = lessonById.get(q.lesson_id);
        return !isFciTopic(meta?.course_name ?? null);
      });

      const windowAttempts = allAttempts.filter((q) => q.created_at >= startIso && q.created_at <= endIso);
      const priorAttempts = allAttempts.filter((q) => q.created_at >= priorStart.toISOString() && q.created_at < priorEnd.toISOString());

      // Daily lesson activity = distinct lesson per day where attempt occurred OR lesson_progress (no ts available).
      const dailyDates: string[] = [];
      const seenLessonByDay = new Set<string>();
      for (const a of windowAttempts) {
        const day = toIsoDate(a.created_at);
        const key = `${day}|${a.lesson_id}`;
        if (!seenLessonByDay.has(key)) {
          seenLessonByDay.add(key);
          dailyDates.push(a.created_at);
        }
      }
      const lessonsByDay = buildDailyBuckets(startDate, endDate, dailyDates);
      const lessonCount = lessonsByDay.reduce((s, r) => s + r.count, 0);
      const lessonsByWeek = bucketByWeek(lessonsByDay);

      const priorDates: string[] = [];
      const priorSeen = new Set<string>();
      for (const a of priorAttempts) {
        const day = toIsoDate(a.created_at);
        const key = `${day}|${a.lesson_id}`;
        if (!priorSeen.has(key)) { priorSeen.add(key); priorDates.push(a.created_at); }
      }
      const priorPeriodLessonsByDay = buildDailyBuckets(priorStart, priorEnd, priorDates);
      const priorLessonCount = priorPeriodLessonsByDay.reduce((s, r) => s + r.count, 0);

      // ---------- Quizzes ----------
      const quizAttempts: QuizRow[] = windowAttempts.map((q) => {
        const meta = lessonById.get(q.lesson_id);
        return {
          id: q.id,
          quizTitle: meta?.lesson_name ?? "Quiz",
          topic: meta?.course_name ?? "General",
          score: Math.round(Number(q.score_percent) || 0),
          passed: !!q.passed,
          attemptNumber: q.attempt_number ?? 1,
          completedAt: q.created_at,
        };
      });
      const averageQuizScore = quizAttempts.length
        ? quizAttempts.reduce((s, q) => s + q.score, 0) / quizAttempts.length
        : 0;
      const firstAttempts = quizAttempts.filter((q) => q.attemptNumber === 1);
      const firstAttemptPasses = firstAttempts.filter((q) => q.passed).length;
      const firstAttemptPassRate = firstAttempts.length ? (firstAttemptPasses / firstAttempts.length) * 100 : 0;
      const retakes = quizAttempts.filter((q) => q.attemptNumber > 1);
      const retakeRate = quizAttempts.length ? (retakes.length / quizAttempts.length) * 100 : 0;

      // Prior period KPI deltas
      const priorQuizScores = priorAttempts.map((q) => Number(q.score_percent) || 0);
      const priorAvgScore = priorQuizScores.length ? priorQuizScores.reduce((a, b) => a + b, 0) / priorQuizScores.length : 0;
      const priorFirsts = priorAttempts.filter((q) => (q.attempt_number ?? 1) === 1);
      const priorPassRate = priorFirsts.length ? (priorFirsts.filter((q) => q.passed).length / priorFirsts.length) * 100 : 0;
      const priorActivityDays = new Set(priorAttempts.map((q) => toIsoDate(q.created_at))).size;

      const activityDays = lessonsByDay.filter((d) => d.count > 0).length;

      // ---------- Topic mastery rollup ----------
      // Aggregate per-topic quiz stats (all-time, non-FCI).
      const perTopic: Map<string, { quizCount: number; scoreSum: number; retakeCount: number; lastAt: string | null }> = new Map();
      for (const a of allAttempts) {
        const meta = lessonById.get(a.lesson_id);
        const topic = meta?.course_name;
        if (!topic || isFciTopic(topic)) continue;
        const cur = perTopic.get(topic) ?? { quizCount: 0, scoreSum: 0, retakeCount: 0, lastAt: null };
        cur.quizCount += 1;
        cur.scoreSum += Number(a.score_percent) || 0;
        if ((a.attempt_number ?? 1) > 1) cur.retakeCount += 1;
        if (!cur.lastAt || a.created_at > cur.lastAt) cur.lastAt = a.created_at;
        perTopic.set(topic, cur);
      }
      // Lessons per topic (completed).
      const completedLessonIds = new Set(((userProgress) || []).map((p) => p.lesson_id));
      const lessonsPerTopic = new Map<string, { completed: number; total: number }>();
      for (const l of (lessonRows) || []) {
        if (isFciTopic(l.course_name)) continue;
        const cur = lessonsPerTopic.get(l.course_name) ?? { completed: 0, total: 0 };
        cur.total += 1;
        if (completedLessonIds.has(l.lesson_id)) cur.completed += 1;
        lessonsPerTopic.set(l.course_name, cur);
      }
      const topicMastery: TopicMasteryRow[] = Array.from(new Set<string>([
        ...((topicMasteryRaw) || []).map((r) => r.topic as string).filter((t) => !isFciTopic(t)),
        ...Array.from(lessonsPerTopic.keys()),
      ])).map((topic) => {
        const m = ((topicMasteryRaw) || []).find((r) => r.topic === topic);
        const q = perTopic.get(topic) ?? { quizCount: 0, scoreSum: 0, retakeCount: 0, lastAt: null };
        const lp = lessonsPerTopic.get(topic) ?? { completed: 0, total: 0 };
        const avg = q.quizCount ? q.scoreSum / q.quizCount : 0;
        const status = masteryStatusFor({ lessonsCompleted: lp.completed, lessonsTotal: lp.total, averageQuizScore: avg, retakeCount: q.retakeCount, quizCount: q.quizCount });
        return {
          topic,
          masteryScore: Number(m?.mastery_score ?? 0),
          lessonsCompleted: lp.completed,
          lessonsTotal: lp.total,
          averageQuizScore: avg,
          quizCount: q.quizCount,
          retakeCount: q.retakeCount,
          lastActivityAt: q.lastAt ?? m?.last_seen_at ?? null,
          masteryStatus: status,
          instructor: getInstructorDisplayForCourse(topic),
        };
      }).sort((a, b) => b.averageQuizScore - a.averageQuizScore);

      // ---------- Practical skills ----------
      const now = Date.now();
      const skillStatuses: SkillStatusRow[] = PRACTICAL_SKILLS.map(({ name, topic }) => {
        const t = topicMastery.find((x) => x.topic === topic);
        let status: SkillStatusRow["status"] = "Not Introduced";
        if (t) {
          const avg = t.averageQuizScore;
          if (t.quizCount > 0 && avg < 60) status = "Needs Review";
          else if (t.quizCount > 0 && avg >= 75 && t.lessonsCompleted > 0) status = "Demonstrated";
          else if (t.quizCount > 0 && avg >= 60) status = "Practicing";
          else if (t.lessonsCompleted > 0) status = "Introduced";
        }
        const firstSeen = t?.lastActivityAt ?? null;
        const newThisMonth = !!firstSeen && (now - new Date(firstSeen).getTime() < 30 * 86400000);
        return { name, topic, status, firstSeenAt: firstSeen, newThisMonth };
      });

      // ---------- Course progress ----------
      const courseProgress: CourseProgressRow[] = ((courseRowsRaw) || [])
        .map((c) => {
          const courseName = (coursesRows)?.find((cc) => cc.course_id === c.course_id)?.course_name ?? c.course_id;
          if (isFciTopic(courseName)) return null;
          const lp = lessonsPerTopic.get(courseName) ?? { completed: 0, total: 0 };
          const tm = topicMastery.find((t) => t.topic === courseName);
          const status: CourseProgressRow["status"] = c.completed_at
            ? "Completed"
            : (c.status === "completed" ? "Completed" : (lp.completed > 0 || c.status === "in_progress" ? "In Progress" : "Not Started"));
          return {
            courseId: c.course_id,
            courseTitle: courseName,
            status,
            completionPercentage: Number(c.completion_percentage) || (lp.total ? Math.round((lp.completed / lp.total) * 100) : 0),
            lessonsCompleted: lp.completed,
            lessonsTotal: lp.total,
            pillar: getPillarForCourse(courseName),
            instructor: getInstructorDisplayForCourse(courseName),
            lastActivityAt: c.updated_at ?? c.started_at ?? null,
            averageQuizScore: tm && tm.quizCount > 0 ? tm.averageQuizScore : null,
            completedAt: c.completed_at ?? null,
          };
        })
        .filter(Boolean) as CourseProgressRow[];

      const pillarTotals = { ...EMPTY_PILLAR_TOTALS } as Record<"Finance" | "Economics" | "Entrepreneurship & Wealth", { inProgress: number; completed: number; total: number }>;
      pillarTotals.Finance = { inProgress: 0, completed: 0, total: 0 };
      pillarTotals.Economics = { inProgress: 0, completed: 0, total: 0 };
      pillarTotals["Entrepreneurship & Wealth"] = { inProgress: 0, completed: 0, total: 0 };
      for (const c of courseProgress) {
        pillarTotals[c.pillar].total += 1;
        if (c.status === "Completed") pillarTotals[c.pillar].completed += 1;
        else if (c.status === "In Progress") pillarTotals[c.pillar].inProgress += 1;
      }
      const coursesCompleted = courseProgress.filter((c) => c.status === "Completed").length;
      const coursesInProgress = courseProgress.filter((c) => c.status === "In Progress").length;

      // ---------- Activity calendar (12 weeks) ----------
      const calStart = new Date(Date.now() - 84 * 86400000);
      const calEnd = new Date();
      const calDates: string[] = [];
      const seenCal = new Set<string>();
      for (const a of allAttempts) {
        const dayKey = `${toIsoDate(a.created_at)}|${a.lesson_id}`;
        if (seenCal.has(dayKey)) continue;
        seenCal.add(dayKey);
        if (a.created_at >= calStart.toISOString()) calDates.push(a.created_at);
      }
      const activityCalendar = buildDailyBuckets(calStart, calEnd, calDates);

      // Phase 2 — merge lesson_progress activity into the calendar so a learner
      // who completed lessons but no quiz still shows as active in the heatmap.
      const calMap = new Map(activityCalendar.map((d) => [d.date, d.count]));
      const seenLessonProgDay = new Set<string>();
      for (const lp of (lessonProgRaw) || []) {
        const ts = (lp.updated_at ?? lp.created_at) as string | null;
        if (!ts) continue;
        const meta = lessonById.get(lp.lesson_id);
        if (meta && isFciTopic(meta.course_name)) continue;
        const day = toIsoDate(ts);
        const key = `${day}|${lp.lesson_id}`;
        if (seenLessonProgDay.has(key)) continue;
        seenLessonProgDay.add(key);
        if (calMap.has(day)) calMap.set(day, (calMap.get(day) ?? 0) + 1);
      }
      const activityCalendarMerged = activityCalendar.map((d) => ({ date: d.date, count: calMap.get(d.date) ?? 0 }));

      // Merge every activity signal we have for an honest "last active" timestamp.
      const activityTimestamps: string[] = [];
      for (const a of allAttempts) activityTimestamps.push(a.created_at);
      for (const lp of (lessonProgRaw) || []) {
        if (lp.updated_at) activityTimestamps.push(lp.updated_at);
        else if (lp.created_at) activityTimestamps.push(lp.created_at);
      }
      for (const c of (courseRowsRaw) || []) {
        if (c.updated_at) activityTimestamps.push(c.updated_at);
        if (c.started_at) activityTimestamps.push(c.started_at);
        if (c.completed_at) activityTimestamps.push(c.completed_at);
      }
      const lastActiveDate = activityTimestamps.length
        ? activityTimestamps.reduce((max, t) => (t > max ? t : max))
        : null;
      const daysSinceLastActivity = lastActiveDate ? Math.floor((Date.now() - new Date(lastActiveDate).getTime()) / 86400000) : null;
      const streaks = computeStreaks(activityCalendarMerged, lastActiveDate);
      const mostActiveWeekday = mostActiveWeekdayName(activityCalendarMerged);
      const weeklyActivity = bucketByWeek(activityCalendarMerged).slice(-8);
      const avgLessonsPerWeek = weeklyActivity.length ? weeklyActivity.reduce((s, w) => s + w.count, 0) / weeklyActivity.length : 0;

      // ---------- Activity timeline ----------
      const timeline: ActivityEvent[] = [];
      // Lesson completions — drive the Recent Activity feed even when the
      // child only has lesson_progress rows (no quiz/course events yet).
      for (const lp of (lessonProgRaw) || []) {
        if (!(lp as AnyValue)?.completed) continue;
        const meta = lessonById.get(lp.lesson_id);
        if (!meta || isFciTopic(meta.course_name)) continue;
        timeline.push({
          id: `lesson-${lp.lesson_id}`,
          type: "lesson",
          subtype: "LESSON_COMPLETED",
          title: meta.lesson_name,
          detail: meta.course_name ?? "Lesson completed",
          topic: meta.course_name ?? null,
          score: null,
          attemptNumber: null,
          at: (lp.updated_at ?? lp.created_at) as string,
        });
      }
      // Quiz events
      for (const q of allAttempts) {
        const meta = lessonById.get(q.lesson_id);
        const title = meta?.lesson_name ?? "Quiz";
        const topic = meta?.course_name ?? null;
        const isRetake = (q.attempt_number ?? 1) > 1;
        const subtype = q.passed ? (isRetake ? "QUIZ_RETAKEN" : "QUIZ_PASSED") : (isRetake ? "QUIZ_RETAKEN" : "QUIZ_FAILED");
        timeline.push({
          id: `quiz-${q.id}`,
          type: "quiz",
          subtype,
          title,
          detail: `${topic ?? "General"} · Score ${Math.round(Number(q.score_percent) || 0)}% · Attempt ${q.attempt_number ?? 1}`,
          topic,
          score: Math.round(Number(q.score_percent) || 0),
          attemptNumber: q.attempt_number ?? 1,
          at: q.created_at,
        });
      }
      // Course events
      for (const c of (courseRowsRaw) || []) {
        const courseName = (coursesRows)?.find((cc) => cc.course_id === c.course_id)?.course_name ?? c.course_id;
        if (isFciTopic(courseName)) continue;
        if (c.started_at) timeline.push({ id: `course-start-${c.course_id}`, type: "course", subtype: "COURSE_STARTED", title: courseName, detail: getInstructorDisplayForCourse(courseName), topic: courseName, score: null, attemptNumber: null, at: c.started_at });
        if (c.completed_at) timeline.push({ id: `course-done-${c.course_id}`, type: "course", subtype: "COURSE_COMPLETED", title: courseName, detail: getInstructorDisplayForCourse(courseName), topic: courseName, score: null, attemptNumber: null, at: c.completed_at });
      }
      // Achievements
      for (const a of (achRowsRaw) || []) {
        const title = (a as AnyValue).achievements?.name ?? (a as AnyValue).achievements?.title ?? "Achievement";
        timeline.push({ id: `ach-${a.id}`, type: "achievement", subtype: "ACHIEVEMENT_EARNED", title, detail: a.achievements?.description ?? "Milestone reached", topic: null, score: null, attemptNumber: null, at: a.unlocked_at });
      }
      // Beta module completions — major milestones for the beta path.
      const MODULE_LABELS: Record<string, string> = {
        budget_builder: "Budget Builder module",
        credit_management: "Credit Management module",
        scam_defense: "Scam Defense module",
      };
      for (const m of (moduleCompletionsRaw) || []) {
        if (!m?.passed || !m?.completed_at) continue;
        const label = MODULE_LABELS[m.module_key] ?? `${m.module_key} module`;
        timeline.push({
          id: `module-${m.module_key}`,
          type: "course",
          subtype: "COURSE_COMPLETED",
          title: `Completed ${label}`,
          detail: "Beta path milestone",
          topic: label,
          score: null,
          attemptNumber: null,
          at: m.completed_at,
        });
      }
      timeline.sort((x, y) => (y.at > x.at ? 1 : -1));

      // ---------- Insights ----------
      const childName = "your child";
      const strengths: InsightItem[] = [];
      const needsReview: InsightItem[] = [];
      const riskFlags: InsightItem[] = [];

      topicMastery.filter((t) => t.masteryStatus === "Strong").slice(0, 6).forEach((t) => strengths.push({ label: t.topic, detail: `Avg ${Math.round(t.averageQuizScore)}%` }));
      if (firstAttemptPassRate >= 80 && firstAttempts.length > 0) strengths.push({ label: "Strong first-attempt pass rate", detail: `${Math.round(firstAttemptPassRate)}% this period` });
      courseProgress.filter((c) => c.status === "Completed").slice(0, 4).forEach((c) => strengths.push({ label: `Completed: ${c.courseTitle}` }));

      topicMastery.filter((t) => t.quizCount > 0 && t.averageQuizScore < 60).slice(0, 6).forEach((t) => needsReview.push({ label: t.topic, detail: `Avg ${Math.round(t.averageQuizScore)}%` }));
      topicMastery.filter((t) => t.retakeCount >= 3).slice(0, 4).forEach((t) => needsReview.push({ label: `${t.topic} — repeated retakes`, detail: `${t.retakeCount} retakes` }));
      skillStatuses.filter((s) => s.status === "Needs Review").forEach((s) => needsReview.push({ label: `Skill: ${s.name}` }));

      if (daysSinceLastActivity !== null && daysSinceLastActivity >= 7) riskFlags.push({ label: `Inactive for ${daysSinceLastActivity}+ days` });
      // Declining scores over last 5
      const last5 = quizAttempts.slice(-5);
      if (last5.length === 5 && last5.every((q, i) => i === 0 || q.score < last5[i - 1].score)) riskFlags.push({ label: "Quiz scores declining over last 5 attempts" });
      // Same lesson failed 3+ times
      const failByLesson = new Map<string, number>();
      for (const a of allAttempts) if (!a.passed) failByLesson.set(a.lesson_id, (failByLesson.get(a.lesson_id) ?? 0) + 1);
      for (const [lid, n] of failByLesson) {
        if (n >= 3) {
          const meta = lessonById.get(lid);
          riskFlags.push({ label: `Stuck on: ${meta?.lesson_name ?? "a quiz"}`, detail: `${n} failed attempts` });
        }
      }
      courseProgress.filter((c) => c.status === "In Progress" && c.lastActivityAt && (Date.now() - new Date(c.lastActivityAt).getTime()) > 7 * 86400000).slice(0, 3).forEach((c) => riskFlags.push({ label: `Stalled: ${c.courseTitle}` }));

      // ---------- Action recommendations ----------
      const actions: ActionRecommendation[] = [];
      if (daysSinceLastActivity !== null && daysSinceLastActivity >= 7) {
        actions.push({ priority: "Re-engage", title: "Re-engage: Set a Learning Session", reason: `No activity in the last ${daysSinceLastActivity} days.`, prompt: "Say: 'Let's spend 15 minutes on HeirWise tonight — pick a topic that sounds interesting.'" });
      }
      topicMastery.filter((t) => t.quizCount > 0 && t.averageQuizScore < 60).slice(0, 2).forEach((t) => {
        actions.push({ priority: "Review Together", title: `Review ${t.topic} Together`, reason: `${t.topic} has an average quiz score of ${Math.round(t.averageQuizScore)}% — below the 60% review threshold.`, prompt: `Ask: 'Can you walk me through what you learned about ${t.topic}?'`, topic: t.topic });
      });
      courseProgress.filter((c) => c.status === "Completed").slice(0, 1).forEach((c) => {
        actions.push({ priority: "Celebrate", title: `Celebrate: ${c.courseTitle} Complete!`, reason: `${childName} finished the ${c.courseTitle} course.`, prompt: `Say: "I'm proud of you for finishing ${c.courseTitle}. What was the most interesting thing you learned?"`, topic: c.courseTitle });
      });
      topicMastery.filter((t) => t.masteryStatus === "Strong").slice(0, 1).forEach((t) => {
        actions.push({ priority: "Explore Next", title: `Explore beyond ${t.topic}`, reason: `${childName} has Strong mastery in ${t.topic} — ready to go deeper.`, prompt: `Say: "You've got ${t.topic} down. Want to explore something new this week?"`, topic: t.topic });
      });
      const priorityOrder: ActionRecommendation["priority"][] = ["Re-engage", "Review Together", "Celebrate", "Explore Next"];
      actions.sort((a, b) => priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority));

      // ---------- Result ----------
      const totalLessonsAvailable = Array.from(lessonsPerTopic.values()).reduce((s, x) => s + x.total, 0);
      const cumulativeLessons = completedLessonIds.size;
      // All-time quiz history so a single early quiz still shows as a starting point.
      const quizScoreHistory = allAttempts
        .slice()
        .sort((a, b) => (a.created_at > b.created_at ? 1 : -1))
        .map((q) => {
          const meta = lessonById.get(q.lesson_id);
          return {
            date: q.created_at,
            score: Math.round(Number(q.score_percent) || 0),
            quizTitle: meta?.lesson_name ?? "Quiz",
            topic: meta?.course_name ?? "General",
          };
        });

      // All-time lesson-level activity for the chart fallback.
      const lessonActivity = ((lessonProgRaw) || [])
        .map((lp) => {
          const meta = lessonById.get(lp.lesson_id);
          if (!meta || isFciTopic(meta.course_name)) return null;
          return {
            date: (lp.updated_at ?? lp.created_at) as string,
            lessonName: meta.lesson_name,
            topic: meta.course_name,
          };
        })
        .filter(Boolean)
        .sort((a: AnyValue, b: AnyValue) => (a.date > b.date ? 1 : -1)) as { date: string; lessonName: string; topic: string | null }[];

      const recentQuizzes = quizAttempts.slice().sort((a, b) => (a.completedAt > b.completedAt ? -1 : 1)).slice(0, 5);
      const strongestTopic = topicMastery.filter((t) => t.quizCount > 0)[0] ?? null;
      // Lowest scoring topic where the child has actually attempted a quiz — surface ASAP, even after one missed question.
      const weakestTopic = topicMastery
        .filter((t) => t.quizCount > 0)
        .slice()
        .sort((a, b) => a.averageQuizScore - b.averageQuizScore)[0] ?? null;

      // ---------- Phase 2: beta-scoped rollups ----------
      const distinctBetaLessons = new Set<string>();
      for (const lp of (lessonProgRaw) || []) {
        if (LAUNCH_PATH_LESSON_IDS.has(lp.lesson_id)) distinctBetaLessons.add(lp.lesson_id);
      }
      // Quiz attempts also imply lesson activity even without lesson_progress rows.
      for (const q of allAttempts) {
        if (LAUNCH_PATH_LESSON_IDS.has(q.lesson_id)) distinctBetaLessons.add(q.lesson_id);
      }
      const betaLessonsCompleted = distinctBetaLessons.size;

      const completedModuleKeys = new Set<string>();
      for (const row of (moduleCompletionsRaw) || []) {
        if (row.passed && (Object.values(LAUNCH_MODULE_KEYS) as string[]).includes(row.module_key)) {
          completedModuleKeys.add(row.module_key);
        }
      }
      const betaModulesCompleted = completedModuleKeys.size;
      const betaPathCompleted = Math.min(TOTAL_LAUNCH_PATH_STEPS, betaLessonsCompleted + betaModulesCompleted);

      const betaModuleStats: Record<LaunchModuleId, { lessonsCompleted: number; lessonsTotal: number; quizCount: number; averageQuizScore: number; moduleCompleted: boolean; lastActivityAt: string | null }> = {
        budget: { lessonsCompleted: 0, lessonsTotal: 5, quizCount: 0, averageQuizScore: 0, moduleCompleted: completedModuleKeys.has(LAUNCH_MODULE_KEYS.budget), lastActivityAt: null },
        credit: { lessonsCompleted: 0, lessonsTotal: 3, quizCount: 0, averageQuizScore: 0, moduleCompleted: completedModuleKeys.has(LAUNCH_MODULE_KEYS.credit), lastActivityAt: null },
        scam:   { lessonsCompleted: 0, lessonsTotal: 2, quizCount: 0, averageQuizScore: 0, moduleCompleted: completedModuleKeys.has(LAUNCH_MODULE_KEYS.scam),   lastActivityAt: null },
      };
      const perModuleScoreAcc: Record<LaunchModuleId, { sum: number; n: number }> = { budget: { sum: 0, n: 0 }, credit: { sum: 0, n: 0 }, scam: { sum: 0, n: 0 } };
      for (const lid of distinctBetaLessons) {
        const mod = moduleOfLaunchLesson(lid);
        if (mod) betaModuleStats[mod].lessonsCompleted += 1;
      }
      for (const q of allAttempts) {
        const mod = moduleOfLaunchLesson(q.lesson_id);
        if (!mod) continue;
        betaModuleStats[mod].quizCount += 1;
        perModuleScoreAcc[mod].sum += Number(q.score_percent) || 0;
        perModuleScoreAcc[mod].n += 1;
        if (!betaModuleStats[mod].lastActivityAt || q.created_at > betaModuleStats[mod].lastActivityAt) {
          betaModuleStats[mod].lastActivityAt = q.created_at;
        }
      }
      (["budget", "credit", "scam"] as LaunchModuleId[]).forEach((mod) => {
        const acc = perModuleScoreAcc[mod];
        betaModuleStats[mod].averageQuizScore = acc.n ? acc.sum / acc.n : 0;
      });

      setData({
        lessonCount,
        cumulativeLessons,
        lessonsByDay,
        lessonsByWeek,
        priorPeriodLessonsByDay,
        quizAttempts,
        recentQuizzes,
        quizScoreHistory,
        lessonActivity,
        averageQuizScore,
        firstAttemptPassRate,
        retakeRate,
        topicMastery,
        skillStatuses,
        courseProgress,
        coursesInProgress,
        coursesCompleted,
        pillarTotals,
        activityDays,
        currentStreak: streaks.current,
        longestStreak: streaks.longest,
        lastActiveDate,
        daysSinceLastActivity,
        activityCalendar: activityCalendarMerged,
        mostActiveWeekday,
        avgLessonsPerWeek,
        weeklyActivity,
        recentActivity: timeline,
        strengths,
        needsReview,
        riskFlags,
        actionRecommendations: actions.slice(0, 4),
        periodDelta: {
          lessonCount: lessonCount - priorLessonCount,
          averageQuizScore: averageQuizScore - priorAvgScore,
          activityDays: activityDays - priorActivityDays,
          firstAttemptPassRate: firstAttemptPassRate - priorPassRate,
        },
        strongestTopic,
        weakestTopic,
        totalLessonsAvailable,
        betaLessonsCompleted,
        betaModulesCompleted,
        betaPathCompleted,
        betaPathTotal: TOTAL_LAUNCH_PATH_STEPS,
        betaModuleStats,
        dataSufficiency: deriveDataSufficiency({
          lessonActivity,
          quizAttempts,
        } as ParentAnalyticsResult),
        isLoading: false,
        error: null,
      });
    } catch (err: unknown) {
      if (myReq !== reqId.current) return;
      console.error("[useParentAnalytics] failed:", err);
      setData((prev) => ({ ...prev, isLoading: false, error: getErrorMessage(err) ?? "Failed to load analytics" }));
    }
  }, [childId, startDate, endDate]);

  // Debounce changes by 300ms.
  useEffect(() => {
    const handle = setTimeout(() => { void fetchAll(); }, 300);
    return () => clearTimeout(handle);
  }, [fetchAll]);

  // Silence unused-range warning while keeping it part of the public API.
  useMemo(() => range, [range]);

  return useMemo<ParentAnalyticsResult>(() => ({ ...data, refetch: () => void fetchAll() }), [data, fetchAll]);
}

function emptyResult(): Omit<ParentAnalyticsResult, "refetch"> {
  return {
    lessonCount: 0,
    cumulativeLessons: 0,
    lessonsByDay: [],
    lessonsByWeek: [],
    priorPeriodLessonsByDay: [],
    quizAttempts: [],
    recentQuizzes: [],
    quizScoreHistory: [],
    lessonActivity: [],
    averageQuizScore: 0,
    firstAttemptPassRate: 0,
    retakeRate: 0,
    topicMastery: [],
    skillStatuses: [],
    courseProgress: [],
    coursesInProgress: 0,
    coursesCompleted: 0,
    pillarTotals: { Finance: { inProgress: 0, completed: 0, total: 0 }, Economics: { inProgress: 0, completed: 0, total: 0 }, "Entrepreneurship & Wealth": { inProgress: 0, completed: 0, total: 0 } },
    activityDays: 0,
    currentStreak: 0,
    longestStreak: 0,
    lastActiveDate: null,
    daysSinceLastActivity: null,
    activityCalendar: [],
    mostActiveWeekday: null,
    avgLessonsPerWeek: 0,
    weeklyActivity: [],
    recentActivity: [],
    strengths: [],
    needsReview: [],
    riskFlags: [],
    actionRecommendations: [],
    periodDelta: { lessonCount: 0, averageQuizScore: 0, activityDays: 0, firstAttemptPassRate: 0 },
    strongestTopic: null,
    weakestTopic: null,
    totalLessonsAvailable: 0,
    betaLessonsCompleted: 0,
    betaModulesCompleted: 0,
    betaPathCompleted: 0,
    betaPathTotal: TOTAL_LAUNCH_PATH_STEPS,
    betaModuleStats: {
      budget: { lessonsCompleted: 0, lessonsTotal: 5, quizCount: 0, averageQuizScore: 0, moduleCompleted: false, lastActivityAt: null },
      credit: { lessonsCompleted: 0, lessonsTotal: 3, quizCount: 0, averageQuizScore: 0, moduleCompleted: false, lastActivityAt: null },
      scam:   { lessonsCompleted: 0, lessonsTotal: 2, quizCount: 0, averageQuizScore: 0, moduleCompleted: false, lastActivityAt: null },
    },
    dataSufficiency: { level: "empty", aiEligible: false, quizSignalReady: false, hasLearningSignal: false, reason: "No lessons or quizzes recorded yet." },
    isLoading: true,
    error: null,
  };
}

// Suppress unused-warning for daysBetween helper kept for future use.
void daysBetween;